import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import Button from '../ui/Button';

interface FormModalProps {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  submitting?: boolean;
  submitDisabled?: boolean;
  error?: string | null;
  children: ReactNode;
}

/**
 * The one dialog shape the workforce pages use: header, scrolling body, footer
 * with Cancel / primary. The body scrolls inside the viewport so a long member
 * checklist never pushes the buttons off a phone screen.
 */
export default function FormModal({
  title, onClose, onSubmit, submitLabel = 'Save', submitting, submitDisabled, error, children,
}: FormModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={title}>
      <form
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[calc(100dvh-2rem)] flex flex-col"
        onSubmit={e => { e.preventDefault(); onSubmit(); }}
      >
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {children}
          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" size="sm" disabled={submitting || submitDisabled}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}

export const inputClass =
  'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-200';

export function Field({ label, htmlFor, hint, children }: { label: string; htmlFor: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-neutral-500 mt-1">{hint}</p>}
    </div>
  );
}
